import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-194eebea"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "dk-modal__wrapper" }
const _hoisted_2 = ["id", "aria-labelledby"]
const _hoisted_3 = { class: "dk-modal__header" }
const _hoisted_4 = ["title"]
const _hoisted_5 = {
  class: "dk-modal__body",
  tabindex: "0"
}
const _hoisted_6 = { class: "dk-modal__inner" }
const _hoisted_7 = { class: "dk-modal__content" }
const _hoisted_8 = { class: "dk-modal__info-box" }
const _hoisted_9 = {
  key: 0,
  class: "dk-modal__pretitle"
}
const _hoisted_10 = ["id"]
const _hoisted_11 = {
  key: 1,
  class: "dk-modal__description"
}

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';

import { useLockScreen } from '@/composables/lock-screen';
import { useMediaQuery } from '@/composables/media-query';

import { getFocusable } from '@/helpers/accessibility';
import { isAbsoluteLink, openLink } from '@/helpers/link';
import { getDayFromDate } from '@/helpers/date';

import { DKPopup as DKPopupModel, DKPopupRecurrence } from '@/models/DKPopup';
import { LinkTargetType } from '@/models/LinkTargetType';
import { DKLink } from '@/models/DKLink';

import { DKIcon, DKImage, DKButton } from '@dormakaba/dormakaba-components';

interface PopupLocalStorage {
  id: string;
  date: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DKPopup',
  setup(__props) {

const isOpen = ref<boolean>(false);

const { isExtraSmall } = useMediaQuery();
const { setLockScreen } = useLockScreen(isOpen);

const popupSessionStorage = ref<string[]>([]);
const popupLocalStorage = ref<PopupLocalStorage[]>([]);
const modalContainerRef = ref<HTMLDivElement>();
const isVisible = ref<boolean>(false);
const focusable = ref<HTMLElement[]>();
const store = useStore();

const popupData = computed<DKPopupModel | null>(
  () => store.getters['navigation/popup']
);

const link = computed<DKLink | undefined>(() => popupData.value?.link);

const linkTarget = computed<LinkTargetType | undefined>(() => {
  if (!link.value) return;
  return isAbsoluteLink(link.value.link)
    ? LinkTargetType.BLANK
    : LinkTargetType.SELF;
});

const isEveryRecurrence = computed<boolean>(
  () => popupData.value?.recurrence === DKPopupRecurrence.EVERY
);

const isDayRecurrence = computed<boolean>(
  () => popupData.value?.recurrence === DKPopupRecurrence.DAY
);

const imageObjectFit = computed<string>(() =>
  isExtraSmall.value ? 'contain' : 'cover'
);

const handleClose = (callback?: () => void): void => {
  if (!popupData.value) return;
  const { id } = popupData.value;

  isVisible.value = false;
  callback && callback();
  isEveryRecurrence.value ? setSessionItem(id) : setLocalItem(id);

  setTimeout(() => {
    isOpen.value = false;
  }, 500);
};

const setFocus = (
  $event: KeyboardEvent,
  element: HTMLElement | undefined
): void => {
  $event.preventDefault();
  element?.focus();
};

const keydownListener = ($event: KeyboardEvent): void => {
  const keysList: { [key: string]: () => void } = {
    Tab: () => {
      const { target, shiftKey } = $event;

      if (focusable.value) {
        const firstElement = focusable.value[0];
        const lastElement = focusable.value.at(-1);

        shiftKey
          ? target === firstElement && setFocus($event, lastElement)
          : target === lastElement && setFocus($event, firstElement);
      }
    },
  };

  const keyCode: () => void = keysList[$event.code];
  keyCode && keyCode();
};

const setFocusOnFirstElement = (): void => {
  if (modalContainerRef.value) {
    focusable.value = Array.from(
      getFocusable(modalContainerRef.value)
    ) as HTMLElement[];

    focusable.value[0].focus();
  }
};

const handleClickLink = (): void => {
  const goToLink = (): void => {
    if (link.value) {
      openLink(link.value.link);
    }
  };

  linkTarget.value === LinkTargetType.SELF ? handleClose(goToLink) : goToLink();
};

const retrieveLocalPopupStorage = (): void => {
  try {
    const localPopupStorage = localStorage.getItem('popup');
    popupLocalStorage.value = localPopupStorage
      ? JSON.parse(localPopupStorage)
      : [];
  } catch {
    popupLocalStorage.value = [];
  }
};

const retrieveSessionPopupStorage = (): void => {
  try {
    const sessionPopupStorage = sessionStorage.getItem('popup');
    popupSessionStorage.value = sessionPopupStorage
      ? JSON.parse(sessionPopupStorage)
      : [];
  } catch {
    popupSessionStorage.value = [];
  }
};

const setSessionItem = (id: string): void => {
  popupSessionStorage.value.push(id);
  sessionStorage.setItem('popup', JSON.stringify(popupSessionStorage.value));
};

const setLocalItem = (id: string): void => {
  const now = new Date();
  const nowISO = now.toISOString();

  popupLocalStorage.value.push({ id, date: nowISO });
  localStorage.setItem('popup', JSON.stringify(popupLocalStorage.value));
};

const getSessionItem = (id: string): void => {
  retrieveSessionPopupStorage();
  isOpen.value = !popupSessionStorage.value.includes(id);
};

const getLocalItem = (id: string): void => {
  retrieveLocalPopupStorage();
  const savedPopupIndex = popupLocalStorage.value.findIndex(
    (item) => item.id === id
  );

  if (savedPopupIndex === -1) {
    isOpen.value = true;
    return;
  }

  const savedPopup = popupLocalStorage.value.splice(savedPopupIndex, 1)[0];
  const interval = isDayRecurrence.value ? 1 : 7;

  isOpen.value = localCondition(savedPopup.date, interval);
};

const handlePopupOpen = (): void => {
  if (!popupData.value) return;

  const { id } = popupData.value;
  isEveryRecurrence.value ? getSessionItem(id) : getLocalItem(id);
};

const localCondition = (date: string, interval: number): boolean => {
  const intervalSavedDate = new Date();
  const savedDate = new Date(date);
  const today = new Date();

  intervalSavedDate.setDate(savedDate.getDate() + interval);

  const intervalSavedDateStr = getDayFromDate(intervalSavedDate);
  const todayStr = getDayFromDate(today);

  return todayStr === intervalSavedDateStr || today > intervalSavedDate;
};

const cleanLocalStorage = (): void => {
  retrieveLocalPopupStorage();

  popupLocalStorage.value = popupLocalStorage.value.filter(
    (item) => !localCondition(item.date, 7)
  );

  popupLocalStorage.value.length
    ? localStorage.setItem('popup', JSON.stringify(popupLocalStorage.value))
    : localStorage.removeItem('popup');
};

onMounted(cleanLocalStorage);
onMounted(handlePopupOpen);

onBeforeUnmount(() => {
  setLockScreen(false);
});

watch(popupData, handlePopupOpen);

watch(
  () => isOpen.value,
  (val: boolean) => {
    if (val) {
      document.addEventListener('keydown', keydownListener);

      setTimeout(() => {
        isVisible.value = true;
        setFocusOnFirstElement();
      });

      return;
    }

    document.removeEventListener('keydown', keydownListener);
  }
);

return (_ctx: any,_cache: any) => {
  return (isOpen.value && popupData.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass([
      'dk-modal',
      { 'is-visible': isVisible.value, 'dk-modal--only-text': !popupData.value.image },
    ])
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", {
            id: popupData.value.id,
            ref_key: "modalContainerRef",
            ref: modalContainerRef,
            class: "dk-modal__container",
            role: "dialog",
            "aria-modal": true,
            "aria-labelledby": `${popupData.value.id}_label`
          }, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("button", {
                title: _ctx.$t('popup.modal_close'),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (handleClose()))
              }, [
                _createVNode(_unref(DKIcon), {
                  icon: "close",
                  size: 36
                })
              ], 8, _hoisted_4)
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                (popupData.value.image)
                  ? (_openBlock(), _createBlock(_unref(DKImage), {
                      key: 0,
                      class: "dk-modal__image",
                      url: popupData.value.image.url,
                      alt: popupData.value.image.title,
                      stack: "getPopupImageStack",
                      objectFill: imageObjectFit.value,
                      customSizes: true
                    }, null, 8, ["url", "alt", "objectFill"]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    (popupData.value.preTitle)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(popupData.value.preTitle), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("h2", {
                      id: `${popupData.value.id}_label`,
                      class: "dk-modal__title"
                    }, _toDisplayString(popupData.value.title), 9, _hoisted_10),
                    (popupData.value.description)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(popupData.value.description), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _createVNode(_unref(DKButton), {
                    class: "dk-modal__btn-link",
                    href: popupData.value.link.link,
                    target: linkTarget.value,
                    label: popupData.value.link.label,
                    onClick: _withModifiers(handleClickLink, ["prevent"])
                  }, null, 8, ["href", "target", "label"])
                ])
              ])
            ])
          ], 8, _hoisted_2)
        ])
      ], 2))
    : _createCommentVNode("", true)
}
}

})